import { useId, type Dispatch } from 'react'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useTransactionTags } from '../transactions/useTransactions'
import { MerchantField, toPickerMerchant } from '../merchants/picker'
import { useMerchantNames } from '../merchants/useMerchants'
import type { Action } from './builderState'
import { QuietIconButton, RuleSelect, useControlFloor } from './ruleControls'
import { ACTIONS, ACTION_ORDER, type ActionDraft, type ActionKey } from './ruleWire'

/** A category option: `depth` 1 is a child, drawn indented under its parent. */
export type CategoryOption = { id: string; name: string; depth: 0 | 1 }
export type CategorySegment = { label: string; options: CategoryOption[] }

const NONE_TEXT: Partial<Record<ActionKey, string>> = {
  flag_for_review: 'Adds it to the review queue on Transactions.',
  exclude_from_budget: 'Still shown in history, not counted against envelopes.',
  exclude_from_reports: 'Left out of spending reports and trends.',
}

const PLACEHOLDER: Partial<Record<ActionKey, string>> = {
  add_tag: '#tag',
  remove_tag: '#tag',
  set_note: 'Note text',
  append_note: 'Note text',
}

// Non-breaking, so the browser keeps the indent inside a native <option>.
const INDENT = '    '

export function RuleActionRow({
  action,
  i,
  removable,
  categories,
  dispatch,
  describedBy,
}: {
  action: ActionDraft
  i: number
  removable: boolean
  categories: CategorySegment[]
  dispatch: Dispatch<Action>
  /** Id of a note that explains this row's category control. */
  describedBy?: string
}) {
  const h = useControlFloor()
  const phone = useBreakpoint() === 'phone'
  const prefix = `Action ${i + 1}`
  const kind = ACTIONS[action.type].value
  const setValue = (v: string) => dispatch({ t: 'actionValue', i, v })
  const tags = useTransactionTags()
  const listId = useId()
  // Q25: add/remove tag suggest the user's existing tags; free text stays allowed. Loading or failed
  // offers nothing rather than blocking the field.
  const suggests = action.type === 'add_tag' || action.type === 'remove_tag'
  const known = categories.some((s) => s.options.some((o) => o.id === action.value))

  return (
    <li className={phone ? 'rule-action rule-action--phone' : 'rule-action'}>
      <RuleSelect
        className="rule-action__type"
        aria-label={`${prefix}, change`}
        value={action.type}
        onChange={(e) => dispatch({ t: 'actionType', i, v: e.target.value as ActionKey })}
      >
        {ACTION_ORDER.map((k) => (
          <option key={k} value={k}>
            {ACTIONS[k].label}
          </option>
        ))}
      </RuleSelect>
      <div className="rule-action__value">
        {kind === 'category' && (
          <RuleSelect
            className="rule-cond__fill"
            aria-label={`${prefix}, category`}
            aria-describedby={action.value === '' ? describedBy : undefined}
            value={action.value}
            onChange={(e) => setValue(e.target.value)}
          >
            <option value="">Choose category…</option>
            {!known && action.value !== '' && (
              <option value={action.value}>(deleted category)</option>
            )}
            {categories.map((s) => (
              <optgroup key={s.label} label={s.label}>
                {s.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.depth === 1 ? `${INDENT}${o.name}` : o.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </RuleSelect>
        )}
        {kind === 'merchant' && <RuleMerchantValue label={`${prefix}, merchant`} value={action.value} onChange={setValue} />}
        {kind === 'text' && (
          <>
            <input
              className="rule-control"
              style={{ minHeight: h }}
              aria-label={`${prefix}, value`}
              placeholder={PLACEHOLDER[action.type]}
              list={suggests ? listId : undefined}
              autoComplete="off"
              value={action.value}
              onChange={(e) => setValue(e.target.value)}
            />
            {suggests && (
              <datalist id={listId}>
                {(tags.status === 'success' ? tags.data : []).map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            )}
          </>
        )}
        {kind === 'none' && <p className="rule-action__note">{NONE_TEXT[action.type]}</p>}
      </div>
      <QuietIconButton
        className="rule-action__remove"
        blocked={!removable}
        aria-label={`Remove ${prefix.toLowerCase()}`}
        onClick={() => dispatch({ t: 'removeAction', i })}
      />
    </li>
  )
}

/**
 * "Set merchant"'s value: the merchant picker, storing the merchant's ID — the backend refuses anything
 * else. The stored id is read by id (`useMerchantNames`, the same lookup as the rule's sentence), so the
 * field names it once it resolves; an id the server says names no merchant, or names a deleted one (which
 * the engine skips and a save refuses), reads "Unknown merchant" and stays a live picker, so the rule can
 * be repaired.
 */
function RuleMerchantValue({ label, value, onChange }: { label: string; value: string; onChange: (id: string) => void }) {
  const lookup = useMerchantNames(value === '' ? [] : [value])
  const stored = value === '' ? null : lookup(value)
  return (
    <MerchantField
      className="rule-action__merchant"
      // Bordered, chevron at the right edge: the select beside it (Set category) is the look to match.
      skin="control"
      label={label}
      value={value === '' ? null : value}
      current={stored?.status === 'found' ? toPickerMerchant(stored.merchant) : null}
      unresolvedName={stored?.status === 'loading' ? 'Loading merchant…' : stored?.status === 'unavailable' ? 'Merchant unavailable' : undefined}
      // A rule always sets SOME merchant: there is no "Remove" here (the action itself is removable).
      onChange={(id) => {
        if (id !== null) onChange(id)
      }}
    />
  )
}
